import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { captureException } from '@sentry/react';

dayjs.extend(timezone);
dayjs.extend(relativeTime);

// Cache the detected timezone to avoid repeated calculations
let cachedTimezone: string | null = null;

export const formatDateAsPerLocale = (
  date: string,
  tz?: string,
  additionalOptions?: Record<string, string | boolean>
) => {
  const options = {
    ...additionalOptions,
    timeZone: tz,
  };
  return new Date(date).toLocaleDateString(navigator.language, options);
};

export const formatDateTimeAsPerLocale = (
  date: string,
  tz?: string,
  additionalOptions?: Intl.DateTimeFormatOptions
) => {
  const options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...additionalOptions,
    timeZone: tz,
  };
  return new Date(date).toLocaleString(navigator.language, options);
};

/**
 * Gets the user's timezone using Intl API with dayjs fallback.
 * Assumes modern browser environment.
 * Caches the result to avoid repeated calculations.
 */
export const getUserTimezone = (): string => {
  // Return cached timezone if available
  if (cachedTimezone) {
    return cachedTimezone;
  }

  try {
    // Primary: Use Intl.DateTimeFormat (supported in all modern browsers)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone && isValidTimezone(timezone)) {
      cachedTimezone = timezone;
      return timezone;
    }
  } catch (error) {
    captureException(error, {
      tags: { component: 'timezone-detection' },
      extra: { method: 'Intl.DateTimeFormat' },
    });
  }

  try {
    // Fallback: dayjs timezone guess
    const dayjsGuess = dayjs.tz.guess();
    if (dayjsGuess && isValidTimezone(dayjsGuess)) {
      cachedTimezone = dayjsGuess;
      return dayjsGuess;
    }
  } catch (error) {
    captureException(error, {
      tags: { component: 'timezone-detection' },
      extra: { method: 'dayjs.tz.guess' },
    });
  }

  // Ultimate fallback
  cachedTimezone = 'UTC';
  return cachedTimezone;
};

/**
 * Clears the timezone cache. Useful for testing or if timezone changes are detected.
 */
export const clearTimezoneCache = (): void => {
  cachedTimezone = null;
};

/**
 * Validates if a timezone string is supported by both Intl and dayjs
 */
const isValidTimezone = (timezone: string): boolean => {
  try {
    // First check if Intl supports it
    Intl.DateTimeFormat(undefined, { timeZone: timezone });

    // Then check if dayjs can use it
    const testDate = dayjs().tz(timezone);
    return testDate.isValid();
  } catch {
    return false;
  }
};

export const formatDateRelative = (date: string) => {
  const userTimezone = getUserTimezone();
  const utcDate = dayjs(date); // UTC
  const localDate = utcDate.tz(userTimezone);

  return localDate.fromNow();
};

/**
 * Formats a date to year only (YYYY) in UTC timezone
 */
export const formatYearUTC = (date: string): string => {
  return dayjs.utc(date).format('YYYY');
};
